<template>
  <div class="bg-gray-50 rounded-xl p-4">
    <h4 class="font-semibold text-gray-700 mb-3 flex items-center">
      <ArrowDownTrayIcon class="w-4 h-4 mr-2" />
      Export Settings
    </h4>
    
    <div class="space-y-3">
      <div>
        <label class="text-xs text-gray-500 mb-2 block">Format</label>
        <div class="grid grid-cols-2 gap-2">
          <button 
            @click="format = 'png'"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              format === 'png' 
                ? 'bg-primary-500 text-white shadow-md' 
                : 'bg-white text-gray-600 border border-gray-200 hover:border-primary-300'
            ]"
          >
            PNG
          </button>
          <button 
            @click="format = 'jpeg'"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all',
              format === 'jpeg' 
                ? 'bg-primary-500 text-white shadow-md' 
                : 'bg-white text-gray-600 border border-gray-200 hover:border-primary-300'
            ]"
          >
            JPEG
          </button>
        </div>
      </div>
      
      <div v-if="format === 'jpeg'">
        <label class="text-xs text-gray-500 mb-2 block">
          Quality: {{ quality }}%
        </label>
        <input 
          type="range" 
          v-model.number="quality"
          min="10"
          max="100"
          class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
        >
      </div>
      
      <button 
        @click="handleExport"
        :disabled="!cropperReady"
        :class="[
          'w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center',
          cropperReady 
            ? 'bg-gradient-to-r from-primary-500 to-indigo-600 text-white hover:shadow-lg hover:scale-[1.02]' 
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        ]"
      >
        <ArrowDownTrayIcon class="w-5 h-5 mr-2" />
        Export Image
      </button>
      
      <button 
        @click="handleReset"
        class="w-full py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
      >
        Choose Another Image
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ArrowDownTrayIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  cropperReady: {
    type: Boolean,
    required: true
  },
  imageName: {
    type: String,
    required: true
  },
  selectedSize: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['export', 'reset'])

const format = ref('png')
const quality = ref(90)

const generateFilename = () => {
  const originalName = props.imageName
  const lastDotIndex = originalName.lastIndexOf('.')
  
  let nameWithoutExt = originalName
  let originalExt = ''
  
  if (lastDotIndex > 0) {
    nameWithoutExt = originalName.substring(0, lastDotIndex)
    originalExt = originalName.substring(lastDotIndex)
  }
  
  const now = new Date()
  const timestamp = now.getFullYear() +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0') +
    String(now.getHours()).padStart(2, '0') +
    String(now.getMinutes()).padStart(2, '0') +
    String(now.getSeconds()).padStart(2, '0')
  
  const ext = format.value === 'jpeg' ? '.jpg' : `.${format.value}`
  
  return `${nameWithoutExt}-${timestamp}${ext}`
}

const handleExport = () => {
  const now = new Date()
  const timestamp = now.getFullYear() +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0') +
    String(now.getHours()).padStart(2, '0') +
    String(now.getMinutes()).padStart(2, '0') +
    String(now.getSeconds()).padStart(2, '0')
  
  const baseFilename = props.imageName.replace(/\.[^/.]+$/, '')
  const filename = `${baseFilename}-cropped-${timestamp}`
  
  console.log('Exporting with options:', {
    format: format.value,
    quality: quality.value,
    width: props.selectedSize.width,
    height: props.selectedSize.height,
    filename: filename
  })
  
  emit('export', {
    format: format.value,
    quality: quality.value,
    width: props.selectedSize.width,
    height: props.selectedSize.height,
    filename: filename
  })
}

const handleReset = () => {
  emit('reset')
}
</script>
